import { EscriptRequestLine } from '../escript/pharmacy-escript.descriptor';

/** Vendor tag appended to the shared HBC order-number format (see buildEscriptOrderNumber). */
export const COMPUTASSIST_ORDER_SUFFIX = 'CA';

// ─── Request Payload ─────────────────────────────────────────────────────────

export interface ComputAssistPharmacy {
  ramsno: string; // Pharmacy RAMS/BHF number — used as pharmacy identifier
  storeName?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postal?: string;
  isFavourite?: boolean;
}

export interface ComputAssistDoctor {
  name: string;
  practicename: string;
  practiceno: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postal?: string;
  phoneno?: string;
  email?: string;
}

export interface ComputAssistMember {
  mainidno: string;
  name: string;
  surname: string;
  title?: string;
  maindob?: string; // Format: YYYYMMDD
  gender?: string;
  language?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  postal?: string;
  email?: string;
  phoneno?: string;
  cellno?: string;
  medaid1?: string; // Primary medical aid scheme code
  medaidno?: string; // Medical aid membership number
  medaid2?: string;
  medaid3?: string;
}

export interface ComputAssistPatient {
  idno: string;
  relation?: string; // "1" = main member, etc.
  name: string;
  surname: string;
  title?: string;
  gender?: string;
  language?: string;
  dob?: string; // Format: YYYYMMDD
  depcode?: string; // Dependant code
}

export interface ComputAssistScriptItem {
  itemno: string; // 1-based item index
  mix: string; // "0" = not a mixture
  napcd: string; // NAPPI code
  descript: string; // Drug description
  qty: string; // Quantity dispensed (e.g. "30.000")
  original_script?: string; // Repeat source script number
  repeats: string;
  supply: string;
  icd10cd?: string;
  directions?: string;
}

export interface ComputAssistScript {
  sctno: string; // Script number (generated by HB)
  medaid?: string; // Primary medical aid scheme code
  payoff?: string;
  scriptdate: string; // Format: "dd mmm yyyy hh:nn:ss" e.g. "05 Nov 2025 14:10:33"
  test: 'Y' | 'N'; // "Y" in dev/QA, "N" in production
  delivery: string; // "0"=Collect "1"=Deliver "2"=COD "3"=Post "4"=Dropbox
  itemcount: string;
  items: ComputAssistScriptItem[];
}

export interface ComputAssistScriptRequest {
  vendorid: string; // Always "hbridge"
  pharmacy: { ramsno: string };
  doctor: ComputAssistDoctor;
  member: ComputAssistMember;
  patient: ComputAssistPatient;
  script: ComputAssistScript;
  message?: string;
  pdf?: string; // Base64-encoded PDF of the script
}

// ─── Submission Response ─────────────────────────────────────────────────────

export interface ComputAssistSubmitResponse {
  code: number; // 0 = success, 1 = failed
  message: string;
  data: null;
}

// ─── Webhook Response (pharmacy → HB after processing) ───────────────────────

export interface ComputAssistAddressResponse {
  type: string;
  line1?: string;
  line2?: string;
  line3?: string;
  line4?: string;
  code?: string;
}

export interface ComputAssistLineResponse {
  no: string;
  nappi: string;
  originalnappi?: string;
  description: string;
  repeatsleft?: string;
  status: string; // "0"=Processed "1"=Incomplete "2"=Rejected "3"=Contact
  claimed?: string;
  copay?: string;
  claimamount?: string; // Actual field name from webhook payload
}

export interface ComputAssistScriptResponse {
  pharmacy: string; // Pharmacy RAMS number from the response
  scriptno: string; // Our sctno, used to look up the Firestore document
  pharmacyscript?: string;
  dateprocessed?: string;
  patientname?: string;
  items?: string;
  status: string; // "0"=Processed "1"=Incomplete "2"=Rejected "3"=Contact
  custommessage?: string;
  claimed?: string;
  copay?: string;
  claim?: string;
}

export interface ComputAssistWebhookResponse {
  response: {
    vendorid: string;
    script: ComputAssistScriptResponse;
    address?: ComputAssistAddressResponse;
    lines?: ComputAssistLineResponse[];
  };
}

// ─── Status ──────────────────────────────────────────────────────────────────

export enum ComputAssistStatusCode {
  /**
   * OUR baseline (not a webhook code): the synchronous send returned code 0 "accepted", which means
   * ComputAssist has RECEIVED the script (same reasoning as Dis-Chem: API-accept = receipt). The
   * webhook later advances to Processed/Incomplete/Rejected/ContactPharmacy.
   */
  ReceivedByPharmacy = -1,
  Processed = 0,
  Incomplete = 1,
  Rejected = 2,
  ContactPharmacy = 3,
  /** OURS, not CA's: the webhook derives it when the script is Processed but some lines are not. */
  PartiallyProcessed = 4,
}

export const COMPUTASSIST_STATUS_LABELS: Record<ComputAssistStatusCode, string> = {
  [ComputAssistStatusCode.ReceivedByPharmacy]: 'Received by pharmacy',
  [ComputAssistStatusCode.Processed]: 'Script Processed',
  [ComputAssistStatusCode.Incomplete]: 'Incomplete',
  [ComputAssistStatusCode.Rejected]: 'Rejected',
  [ComputAssistStatusCode.ContactPharmacy]: 'Contact Your Pharmacy',
  [ComputAssistStatusCode.PartiallyProcessed]: 'Script partially processed',
};

// ─── Firestore Document Shape ─────────────────────────────────────────────────

export interface ComputAssistStatusHistoryEntry {
  statusCode: number;
  // Absent only on the synthetic "Received by pharmacy" baseline the timeline unshifts
  timestamp?: string;
  // Line split frozen by the webhook at transition time; `lines` on the record is overwritten by later deliveries
  linesProcessed?: number;
  linesTotal?: number;
}

export interface ComputAssistOrderStatusRecord {
  statusCode: number;
  statusUpdatedAt: string;
  statusHistory: ComputAssistStatusHistoryEntry[];
  // Script-level fields (populated by webhook)
  pharmacy?: string; // Pharmacy RAMS number from the response
  pharmacyscript?: string;
  dateprocessed?: string;
  patientname?: string;
  items?: string;
  custommessage?: string;
  // Financial summary (populated by webhook)
  claimed?: string;
  copay?: string;
  claim?: string;
  // Per-item line statuses (populated by webhook)
  lines?: ComputAssistLineResponse[];
  // What HbC sent, unpadded NAPPI as CA echoes it (written at send time)
  requestLines?: EscriptRequestLine[];
}

// ─── Script Request Wrapper ───────────────────────────────────────────────────

export interface ComputAssistSendScriptRequest {
  prescription?: any;
  scriptPdfBase64?: string;
  pharmacy?: ComputAssistPharmacy;
  practiceTenantId?: string;
  encounterId?: string;
  patientId?: string;
}

// ─── Timeline UI ─────────────────────────────────────────────────────────────

export type ComputAssistTimelineStepState = 'completed' | 'active' | 'pending' | 'rejected' | 'partial';

export interface ComputAssistTimelineStep {
  statusCode: number;
  label: string;
  icon: string;
  description: string;
  state: ComputAssistTimelineStepState;
  timestamp?: string;
}

const COMPUTASSIST_STATUS_META: Record<number, { icon: string; description: string }> = {
  [ComputAssistStatusCode.ReceivedByPharmacy]: {
    icon: 'inbox',
    description: 'ComputAssist has received the prescription — awaiting processing by the pharmacy.',
  },
  [ComputAssistStatusCode.Processed]: { icon: 'check_circle', description: 'Script accepted and processed by the pharmacy.' },
  [ComputAssistStatusCode.Incomplete]: {
    icon: 'schedule',
    description: 'Script processing is incomplete. Contact the pharmacy for details.',
  },
  [ComputAssistStatusCode.Rejected]: { icon: 'cancel', description: 'Script was rejected by the pharmacy.' },
  [ComputAssistStatusCode.ContactPharmacy]: { icon: 'phone', description: 'Please contact your pharmacy directly regarding this script.' },
  [ComputAssistStatusCode.PartiallyProcessed]: {
    icon: 'rule',
    description: 'Some items were not processed by the pharmacy - see medication lines below.',
  },
};

/** Synthetic "we submitted" step — not a ComputAssist status code, so it can't collide with 0..3. */
const COMPUTASSIST_SENT_STEP_CODE = -2;

/** Spell out the split behind a PartiallyProcessed step; undefined (generic meta text) when the split is unknown. */
function partiallyProcessedDescription(processed: number | undefined, total: number | undefined): string | undefined {
  if (!total) {
    return undefined;
  }
  const unprocessed = total - (processed ?? 0);
  const items = unprocessed === 1 ? 'item was' : 'items were';
  return `${processed ?? 0} of ${total} items processed. ${unprocessed} ${items} not processed by the pharmacy - see medication lines below.`;
}

/** Prefer the split frozen on the history entry; the record's `lines` only describe the CURRENT status. */
function lineSplitFor(
  entry: ComputAssistStatusHistoryEntry,
  isCurrent: boolean,
  lines: ComputAssistLineResponse[]
): { processed?: number; total?: number } {
  if (entry.linesTotal) {
    return { processed: entry.linesProcessed, total: entry.linesTotal };
  }
  if (isCurrent && lines.length) {
    return { processed: lines.filter(line => Number(line.status) === ComputAssistStatusCode.Processed).length, total: lines.length };
  }
  return {};
}

export interface ComputAssistLineOrigin {
  substituted: boolean;
  /** Description of the prescribed item this line replaced, when it can be resolved. */
  replaces?: string;
}

/** Leading zeros are padding: '000123456' and '123456' are the same code. */
export const unpadNappi = (nappi: string | null | undefined): string => String(nappi ?? '').replace(/^0+/, '');

/**
 * A returned line whose NAPPI is not one we sent is a pharmacy substitution. CA's `originalnappi`
 * names what it replaced when populated; otherwise fall back to our request lines.
 */
export function describeComputAssistLineOrigin(
  line: Pick<ComputAssistLineResponse, 'nappi' | 'originalnappi'>,
  record: Pick<ComputAssistOrderStatusRecord, 'requestLines'>
): ComputAssistLineOrigin {
  const requested = record.requestLines ?? [];
  const nappi = unpadNappi(line.nappi);
  const original = unpadNappi(line.originalnappi);
  const differsFromOriginal = !!original && original !== nappi;
  // A blank echoed NAPPI can't prove a substitution (unmatchable against the request lines)
  const notRequested = !!nappi && requested.length > 0 && !requested.some(r => unpadNappi(r.nappi) === nappi);
  if (!differsFromOriginal && !notRequested) {
    return { substituted: false };
  }
  const replaces = original ? requested.find(r => unpadNappi(r.nappi) === original)?.description : undefined;
  return { substituted: true, ...(replaces && { replaces }) };
}

export function buildComputAssistTimelineSteps(statusRecord: ComputAssistOrderStatusRecord): ComputAssistTimelineStep[] {
  const { statusCode: current = ComputAssistStatusCode.ReceivedByPharmacy, statusHistory = [], statusUpdatedAt, lines = [] } = statusRecord;

  // Synthetic first step: our submission act, always completed (same pattern as Dis-Chem / EasyRx).
  const sentStep: ComputAssistTimelineStep = {
    statusCode: COMPUTASSIST_SENT_STEP_CODE,
    label: 'Sent to pharmacy',
    icon: 'send',
    description: 'Prescription submitted to ComputAssist.',
    state: 'completed',
  };

  // One step per history entry (the webhook appends on status OR line-picture change); a consecutive
  // repeat with no frozen split is a legacy re-delivery of the same status and collapses.
  const entries: ComputAssistStatusHistoryEntry[] = statusHistory.filter(
    (entry, index) => index === 0 || entry.statusCode !== statusHistory[index - 1].statusCode || entry.linesTotal != null
  );
  if (!entries.some(h => h.statusCode === current)) {
    entries.push({ statusCode: current, timestamp: statusUpdatedAt });
  }
  // A successful send = ComputAssist received it: always show the "Received by pharmacy" baseline,
  // even before any webhook advances the order (the streamed doc no longer seeds a status).
  if (!entries.some(h => h.statusCode === ComputAssistStatusCode.ReceivedByPharmacy)) {
    entries.unshift({ statusCode: ComputAssistStatusCode.ReceivedByPharmacy });
  }

  return [
    sentStep,
    ...entries.map((entry, index) => {
      const code = entry.statusCode;
      const meta = COMPUTASSIST_STATUS_META[code];
      const isCurrent = index === entries.length - 1;
      const isRepeat = index > 0 && entries[index - 1].statusCode === code;
      const isRejected = code === ComputAssistStatusCode.Rejected;
      const isDone = code === ComputAssistStatusCode.Processed;
      const isPartial = code === ComputAssistStatusCode.PartiallyProcessed;
      const split = isPartial ? lineSplitFor(entry, isCurrent, lines) : {};

      let state: ComputAssistTimelineStepState;
      if (isRejected) {
        state = 'rejected';
      } else if (isPartial) {
        state = 'partial';
      } else if (isCurrent && isDone) {
        state = 'completed';
      } else if (isCurrent) {
        state = 'active';
      } else {
        state = 'completed';
      }

      const label = COMPUTASSIST_STATUS_LABELS[code] ?? `Status ${code}`;
      return {
        statusCode: code,
        label: isRepeat ? `${label} (updated)` : label,
        icon: meta?.icon ?? 'help_outline',
        description:
          partiallyProcessedDescription(split.processed, split.total) ??
          (isRepeat ? 'The pharmacy updated the medication lines - see below.' : (meta?.description ?? '')),
        state,
        timestamp: entry.timestamp,
      };
    }),
  ];
}
